import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbEye, TbEyeOff, TbLink, TbTrash } from 'react-icons/tb';

import { useNestedResourceAccess } from '@/hooks/useNestedResourceAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    getWorkspaceResourceTypeLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useListStore, useListViewStore } from '@/stores';
import { ListView } from '@/types';

import { WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface ListViewActionsDropdownProps {
    listView: ListView;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
}

const ListViewActionsDropdown: FC<ListViewActionsDropdownProps> = ({
    children,
    listView,
    onOpenChange,
}): JSX.Element => {
    const [renameVisible, setRenameVisible] = useState(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);

    const { selectedList } = useListStore();

    const { toggleListViewArchive, duplicateListView, toggleListViewLock, updateListView } = useListViewStore();
    const access = useNestedResourceAccess('listViews', listView?.id);
    const resourceTypeLabel = getWorkspaceResourceTypeLabel('list-view');

    const handleToggleLock = async () => {
        if (!access.canManage) {
            return;
        }

        toggleListViewLock(listView?.id, !listView?.locked);
    };

    const handleToggleArchive = async () => {
        if (!access.canManage || access.locked) {
            return;
        }

        toggleListViewArchive(listView?.id, !listView?.archived);
    };

    const handleToggleHidden = async () => {
        if (!access.canUpdate) {
            return;
        }

        updateListView(listView?.id, { hidden: !listView?.hidden });
    };

    const handleDuplicateListView = async () => {
        if (!access.canRead || !access.canCreateSibling) {
            return;
        }

        duplicateListView(listView?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        // {
        //     key: 'favorited',
        //     icon: listView?.favorited ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
        //     label: listView?.favorited ? 'Remove from Favorites' : 'Add to Favorites',
        //     onClick: handleToggleFavorite,
        // },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={listView?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', listView?.locked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={listView?.archived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', listView?.archived),
            onClick: handleToggleArchive,
            permission: 'manage',
            disabled: access.locked,
        },
        {
            key: 'hidden',
            icon: listView?.hidden ? <TbEyeOff size={'1rem'} /> : <TbEye size={'1rem'} />,
            label: listView?.hidden ? 'Show' : 'Hide',
            onClick: handleToggleHidden,
            permission: 'update',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/lists/${selectedList?.id}?view=${listView?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: listView?.name,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: listView?.name,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        // {
        //     key: 'openInNewWindow',
        //     icon: <TbExternalLink size={'1rem'} />,
        //     label: 'Open in New Window',
        //     onClick: () => {
        //         window.open(`/investments/${listView?.id}`, '_blank');
        //     },
        // },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateListView,
            permission: 'read',
            disabled: !access.canCreateSibling,
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/investments/${listView?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Investment ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.name,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'moveToFolder',
        //     icon: <TbFolderSymlink size='1rem' />,
        //     label: 'Move to Folder',
        //     onClick: () => {
        //         setMoveToFolderVisible(true);
        //     },
        // },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: 'Delete',
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
            disabled: access.locked,
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{ resourceId: listView.id, resourceType: 'listViews', title: listView.name }}
                items={items}
                menuStyle={{
                    marginTop: '0.5rem',
                    minWidth: 280,
                }}
                placement='bottomLeft'
                resourceLabel={resourceTypeLabel}
                stopPropagation
                onOpenChange={onOpenChange}
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            <RenameResourceModal
                open={renameVisible}
                setOpen={setRenameVisible}
                resource={listView}
                singularResource='list-view'
            />
            <MoveResourceToFolderModal
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
                resource={listView}
                singularResource='list-view'
            />
            <DeleteResourceModal
                open={deleteVisible}
                setOpen={setDeleteVisible}
                resource={listView}
                singularResource='list-view'
            />
        </>
    );
};

export default ListViewActionsDropdown;
